import { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/api/client";
import type { DashboardSummaryResponse } from "@/api/types";
import { Link } from "react-router-dom";

type RiskLevel = "high" | "medium" | "low";

interface DistrictRow {
  id: number;
  name: string;
  region: string;
  predicted2wk: number;
  growthPct: number;
  riskLevel: RiskLevel;
  riskTrend: "Rising" | "Stable" | "Declining";
}

type SortField = "name" | "predicted2wk" | "growthPct" | "riskLevel";
type SortDir = "asc" | "desc";

function getRiskPriority(level: RiskLevel) {
  return level === "high" ? 3 : level === "medium" ? 2 : 1;
}

export default function Reports() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState<"summary" | "comparison">("summary");
  const [regionFilter, setRegionFilter] = useState("");
  const [sortField, setSortField] = useState<SortField>("predicted2wk");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [exportToast, setExportToast] = useState(false);
  const [loading, setLoading] = useState(true);

  const [districtMap, setDistrictMap] = useState<DashboardSummaryResponse["district_map"]>([]);
  const [weeklyTrend, setWeeklyTrend] = useState<{ week: string; cases: number }[]>([]);
  const [activeAlertCount, setActiveAlertCount] = useState(0);
  const [resourceTotals, setResourceTotals] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [summary, casesData, alertsData, resourcesData] = await Promise.all([
          api.dashboardSummary() as Promise<DashboardSummaryResponse>,
          api.malariaCases({ per_page: 200 }) as Promise<{ items: { week_start: string; confirmed_cases: number }[] }>,
          api.alerts({ per_page: 100 }) as Promise<{ items: { status: string }[] }>,
          api.resources({ per_page: 500 }) as Promise<{ items: { resource_type: string; quantity: number }[] }>,
        ]);
        if (cancelled) return;

        setDistrictMap(summary.district_map ?? []);

        const weeks: Record<string, number> = {};
        for (const c of casesData?.items ?? []) {
          weeks[c.week_start] = (weeks[c.week_start] || 0) + c.confirmed_cases;
        }
        setWeeklyTrend(
          Object.entries(weeks)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-8)
            .map(([date, cases]) => ({ week: date.slice(5), cases }))
        );

        const active = (alertsData?.items ?? []).filter((a) => a.status === "active").length;
        setActiveAlertCount(active);

        const totals: Record<string, number> = {};
        for (const r of resourcesData?.items ?? []) {
          totals[r.resource_type] = (totals[r.resource_type] || 0) + r.quantity;
        }
        setResourceTotals(totals);
      } catch {
        // silently show empty state
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const regions = useMemo(() => Array.from(new Set(districtMap.map((d) => d.region))).sort(), [districtMap]);

  const districtRows: DistrictRow[] = useMemo(() => {
    return districtMap.map((d) => {
      const risk = (d.risk_level ?? "low") as RiskLevel;
      const growthPct = 0;
      const trend: DistrictRow["riskTrend"] = risk === "high" ? "Rising" : risk === "medium" ? "Stable" : "Declining";
      return {
        id: d.district_id,
        name: d.district_name,
        region: d.region,
        predicted2wk: d.predicted_cases ?? 0,
        growthPct,
        riskLevel: risk,
        riskTrend: trend,
      };
    });
  }, [districtMap]);

  const filteredRows = useMemo(() => {
    let data = regionFilter ? districtRows.filter((r) => r.region === regionFilter) : [...districtRows];
    data.sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") cmp = a.name.localeCompare(b.name);
      else if (sortField === "predicted2wk") cmp = a.predicted2wk - b.predicted2wk;
      else if (sortField === "growthPct") cmp = a.growthPct - b.growthPct;
      else if (sortField === "riskLevel") cmp = getRiskPriority(a.riskLevel) - getRiskPriority(b.riskLevel);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return data;
  }, [districtRows, regionFilter, sortField, sortDir]);

  const highRiskCount = filteredRows.filter((r) => r.riskLevel === "high").length;
  const totalPredicted = filteredRows.reduce((s, r) => s + r.predicted2wk, 0);

  const regionBreakdown = useMemo(() => {
    const map: Record<string, { high: number; medium: number; low: number }> = {};
    for (const r of filteredRows) {
      if (!map[r.region]) map[r.region] = { high: 0, medium: 0, low: 0 };
      map[r.region][r.riskLevel] += 1;
    }
    return Object.entries(map).map(([region, counts]) => ({ region, ...counts }));
  }, [filteredRows]);

  const tooltipStyle = theme === "dark"
    ? { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px", fontSize: "11px", color: "#e2e8f0" }
    : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "11px", color: "#1e293b" };
  const gridStroke = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const tickFill = theme === "dark" ? "#64748b" : "#94a3b8";

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const exportCSV = () => {
    const headers = [t("reports_district"), t("reports_region"), t("reports_predicted_2wk"), t("reports_alert_level"), t("reports_risk_trend")];
    const rows = filteredRows.map((r) => [r.name, r.region, r.predicted2wk, r.riskLevel, r.riskTrend]);
    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `malariaguard-reports-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 2500);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <i className="ri-arrow-up-down-line text-[10px] text-slate-400 dark:text-slate-600 ml-1" />;
    return <i className={`text-[10px] ml-1 ${sortDir === "asc" ? "ri-arrow-up-line text-teal" : "ri-arrow-down-line text-teal"}`} />;
  };

  const riskBadge: Record<RiskLevel, string> = {
    high: "bg-risk-high/15 text-risk-high border-risk-high/20",
    medium: "bg-amber/15 text-amber border-amber/20",
    low: "bg-risk-low/15 text-risk-low border-risk-low/20",
  };
  const trendColor: Record<string, string> = { Rising: "text-risk-high", Stable: "text-amber", Declining: "text-risk-low" };
  const trendIcon: Record<string, string> = { Rising: "ri-arrow-up-line", Stable: "ri-subtract-line", Declining: "ri-arrow-down-line" };

  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("reports_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("reports_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 focus:outline-none focus:border-teal/50"
          >
            <option value="">{t("reports_all_regions")}</option>
            {regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-line" />
            {t("reports_export_csv")}
          </button>
        </div>
      </div>

      {exportToast && (
        <div className="fixed bottom-5 right-5 z-50 animate-slide-up">
          <div className="bg-teal-muted/80 backdrop-blur-sm border border-teal/30 rounded-lg px-4 py-3 flex items-center gap-2 shadow-glow">
            <i className="ri-check-line text-teal" />
            <span className="text-sm text-teal">{t("reports_exported")}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="min-h-[200px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: t("reports_total_districts"), value: filteredRows.length, icon: "ri-map-pin-line", color: "text-slate-700 dark:text-slate-300" },
              { label: t("reports_high_risk"), value: highRiskCount, icon: "ri-error-warning-line", color: "text-risk-high" },
              { label: t("reports_cases_this_week"), value: totalPredicted.toLocaleString(), icon: "ri-virus-line", color: "text-teal" },
              { label: t("reports_active_interventions_metric"), value: activeAlertCount, icon: "ri-shield-check-line", color: "text-amber" },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className={`${kpi.icon} text-sm text-slate-400 dark:text-slate-500`} />
                  <span className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{kpi.label}</span>
                </div>
                <p className={`text-lg font-bold font-display ${kpi.color}`}>{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 w-fit">
            {(["summary", "comparison"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  activeTab === tab
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                <i className={`${tab === "summary" ? "ri-file-list-3-line" : "ri-bar-chart-grouped-line"} mr-1.5`} />
                {tab === "summary" ? t("reports_outbreak_summary") : t("reports_district_comparison")}
              </button>
            ))}
          </div>

          {activeTab === "summary" && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("reports_weekly_trend")}</h3>
                    <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">{t("reports_national_confirmed_8wk")}</p>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-teal-muted/30 text-teal border border-teal/15 font-mono">{t("common_live")}</span>
                </div>
                {weeklyTrend.length === 0 ? (
                  <div className="h-[260px] flex items-center justify-center text-xs text-slate-400 dark:text-slate-600">No case data available yet</div>
                ) : (
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={weeklyTrend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis dataKey="week" tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value.toLocaleString(), t("alerts_cases")]} />
                        <Line type="monotone" dataKey="cases" stroke="#2dd4bf" strokeWidth={2.5} dot={{ r: 3, fill: "#2dd4bf" }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-4">{t("reports_outbreak_metrics")}</h3>
                  <div className="space-y-3">
                    {[
                      { label: t("reports_active_interventions_metric"), value: activeAlertCount, icon: "ri-shield-check-line", color: "text-teal" },
                      { label: "RDT Kits in Stock", value: (resourceTotals["rdt_kits"] ?? 0).toLocaleString(), icon: "ri-test-tube-line", color: "text-amber" },
                      { label: "ACT Drugs in Stock", value: (resourceTotals["act_drugs"] ?? 0).toLocaleString(), icon: "ri-capsule-line", color: "text-emerald" },
                      { label: "Mosquito Nets in Stock", value: (resourceTotals["mosquito_nets"] ?? 0).toLocaleString(), icon: "ri-shield-line", color: "text-teal" },
                      { label: "Trained Personnel", value: (resourceTotals["trained_personnel"] ?? 0).toLocaleString(), icon: "ri-user-heart-line", color: "text-slate-700 dark:text-slate-300" },
                    ].map((m) => (
                      <div key={m.label} className="flex items-center justify-between py-2 border-b border-slate-200/50 dark:border-slate-800/50 last:border-0">
                        <div className="flex items-center gap-2">
                          <i className={`${m.icon} text-xs text-slate-400 dark:text-slate-500`} />
                          <span className="text-xs text-slate-500 dark:text-slate-400">{m.label}</span>
                        </div>
                        <span className={`text-sm font-semibold font-mono ${m.color}`}>{m.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-4">{t("reports_risk_by_region")}</h3>
                  {regionBreakdown.length === 0 ? (
                    <div className="h-[180px] flex items-center justify-center text-xs text-slate-400 dark:text-slate-600">No data</div>
                  ) : (
                    <div className="h-[180px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={regionBreakdown} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                          <XAxis dataKey="region" tick={{ fontSize: 9, fill: tickFill }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fill: tickFill }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={tooltipStyle} />
                          <Bar dataKey="high" stackId="a" fill="#ef4444" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="medium" stackId="a" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="low" stackId="a" fill="#22c55e" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-4 mt-3">
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-risk-high" /><span className="text-[10px] text-slate-500 dark:text-slate-500">{t("common_high")}</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-risk-medium" /><span className="text-[10px] text-slate-500 dark:text-slate-500">{t("common_medium")}</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-risk-low" /><span className="text-[10px] text-slate-500 dark:text-slate-500">{t("common_low")}</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "comparison" && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("reports_district_comparison")}</h3>
                  <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">
                    {t("reports_comparing", { count: filteredRows.length })}
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-2 text-[10px] text-slate-400 dark:text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-high" /> {t("common_high")}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-medium" /> {t("common_medium")}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-low" /> {t("common_low")}</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                      {([
                        { key: "name" as SortField, label: t("reports_district") },
                        { key: "predicted2wk" as SortField, label: t("reports_predicted_2wk") },
                        { key: "riskLevel" as SortField, label: t("reports_alert_level") },
                      ]).map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:hover:text-slate-300 transition-colors select-none"
                        >
                          <span className="flex items-center gap-1">{col.label}<SortIcon field={col.key} /></span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_risk_trend")}</th>
                      <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("reports_actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{r.name}</p>
                            <p className="text-[10px] text-slate-400 dark:text-slate-500">{r.region}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300">
                          {r.predicted2wk > 0 ? r.predicted2wk.toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${riskBadge[r.riskLevel]}`}>
                            {r.riskLevel === "high" ? t("common_high") : r.riskLevel === "medium" ? t("common_medium") : t("common_low")}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`flex items-center gap-1 text-xs font-medium ${trendColor[r.riskTrend]}`}>
                            <i className={`${trendIcon[r.riskTrend]} text-[10px]`} />
                            {r.riskTrend}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Link
                              to={`/predictions?district=${r.id}`}
                              className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-teal hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                              title={t("reports_view_predictions")}
                            >
                              <i className="ri-line-chart-line text-xs" />
                            </Link>
                            <Link
                              to={`/alerts?district=${r.id}`}
                              className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 dark:text-slate-500 hover:text-amber hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                              title={t("reports_view_alerts")}
                            >
                              <i className="ri-alarm-warning-line text-xs" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-500">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 mx-auto mb-3">
                            <i className="ri-inbox-line text-xl text-slate-400 dark:text-slate-500" />
                          </div>
                          {t("reports_no_data")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-500">
                <span>{t("reports_showing_districts", { count: filteredRows.length, total: districtRows.length })}</span>
                {regionFilter && (
                  <button onClick={() => setRegionFilter("")} className="text-teal hover:text-teal-hover transition-colors">
                    {t("reports_clear_region")}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}